import { useEffect, useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CANONICAL_BOOKS } from '@/lib/bible/active';
import { SCRIPTURE_JOURNAL_ENABLED } from '@/lib/feature-flags';
import { HL_DOT_HEX } from '@/lib/highlight-utils';
import { chapterSummariesForBook } from '@/lib/journal-aggregate';
import { useJournalSources } from '@/hooks/useJournalSources';
import BibleThemePicker from '@/components/BibleThemePicker/BibleThemePicker';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import JournalSourceState from '@/components/JournalSourceState/JournalSourceState';
import { useSettingsStore } from '@/lib/settings-store';
import styles from './JournalBookPage.module.css';

/**
 * Scripture Journal Book page at /bible/bookmarks/book/:bookSlug (canon-spatial
 * redesign Phase 1, §6). A leaf route (rule #10: own back Link, native browser
 * back, NO NavStack layer). Lists every chapter of the book that carries a mark
 * (ascending), each a peek (count + highlight family dots + note/prayer
 * indicators) linking to the passage page.
 *
 * Pure structure from the journal-aggregate leaf; no BSB load here (the passage
 * page resolves verse text). Bad slug -> Navigate home; no marks -> Navigate
 * home, but only once sources have settled (the hook's `ready`) so a direct
 * load never redirects out of a valid book mid-hydration (codex BLOCKER).
 */
export default function JournalBookPage() {
  const { t } = useTranslation();
  const { bookSlug = '' } = useParams();
  const { sources, state: sourceState, ready, retry } = useJournalSources();
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  // App notice stack (v0.2.0 finishing): an in-flow spacer at the top of the
  // page (above the Back link and the theme picker, so the picker's menu also
  // opens below the notices) keeps the page below the fixed notices at the
  // top; 0 while no notice shows. Each render path mounts its own spacer with
  // the same callback ref.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const book = useMemo(
    () => CANONICAL_BOOKS.find((b) => b.slug === bookSlug),
    [bookSlug],
  );

  const chapters = useMemo(
    () => (book ? chapterSummariesForBook(sources, book.slug) : []),
    [sources, book],
  );

  useEffect(() => {
    if (!book) return;
    const prior = document.title;
    document.title = book.bookTitle ?? book.name;
    return () => {
      document.title = prior;
    };
  }, [book]);

  if (!SCRIPTURE_JOURNAL_ENABLED)
    return <Navigate to="/bible" replace />;
  // Unknown book is static (slug not in canon), safe to redirect pre-hydration.
  if (!book) return <Navigate to="/bible/bookmarks" replace />;
  // Wait for the source loads to settle before treating empty as truly empty.
  if (!ready)
    return (
      <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
        <div className={styles.readerBg} aria-hidden="true" />
        <div className={styles.container} data-bible-theme={bibleTheme}>
          <div
            ref={noticeReserveRef}
            className={styles.noticeReserve}
            style={{ height: noticeReserve }}
            data-notice-reserve={noticeReserve}
            aria-hidden="true"
          />
          <div className={styles.topBar}>
            <Link to="/bible/bookmarks" className={styles.backLink}>
              {t('bible.back')}
            </Link>
            <div className={styles.headerActions}>
              <BibleThemePicker />
            </div>
          </div>
          <h1 className={styles.title}>{book.bookTitle ?? book.name}</h1>
          <JournalSourceState state={sourceState} onRetry={retry} />
        </div>
      </div>
    );
  // A book with no marked chapters left (e.g. its last mark was just deleted)
  // -> back to the journal home.
  if (chapters.length === 0) return <Navigate to="/bible/bookmarks" replace />;

  return (
    <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
      <div className={styles.readerBg} aria-hidden="true" />
      <div className={styles.container} data-bible-theme={bibleTheme}>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <div className={styles.topBar}>
        <Link to="/bible/bookmarks" className={styles.backLink}>
          {t('bible.back')}
        </Link>
        <div className={styles.headerActions}>
          <BibleThemePicker />
        </div>
      </div>
      <h1 className={styles.title}>{book.bookTitle ?? book.name}</h1>
      <p className={styles.subtitle}>
        {t('bookmark.bookPageChapterCount', { count: chapters.length })}
      </p>

      <ul className={styles.list}>
        {chapters.map((ch) => (
          <li key={ch.chapter} className={styles.rowItem}>
            <Link
              to={`/bible/bookmarks/book/${book.slug}/${ch.chapter}`}
              className={styles.row}
              style={{
                borderLeftColor: ch.families[0]
                  ? HL_DOT_HEX[ch.families[0]]
                  : 'var(--reader-accent)',
              }}
              aria-label={t('bible.chapterAriaLabel', {
                book: book.name,
                chapter: ch.chapter,
              })}
            >
              <div className={styles.rowTop}>
                <span className={styles.rowRef}>
                  {book.name} {ch.chapter}
                </span>
                <span className={styles.rowCount}>
                  {t('bookmark.chapterMarkCount', { count: ch.markCount })}
                </span>
              </div>
              <div className={styles.peekMeta} aria-hidden="true">
                {ch.families.map((fam) => (
                  <span
                    key={fam}
                    className={styles.dot}
                    style={{ backgroundColor: HL_DOT_HEX[fam] }}
                  />
                ))}
                {ch.noteCount > 0 && (
                  <span className={styles.indicator}>
                    <span className={styles.noteGlyph} aria-hidden="true">
                      ✎
                    </span>
                    {ch.noteCount}
                  </span>
                )}
                {ch.prayerCount > 0 && (
                  <span className={styles.indicator}>
                    <span className={styles.prayerGlyph} aria-hidden="true">
                      ✦
                    </span>
                    {ch.prayerCount}
                  </span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
      </div>
    </div>
  );
}
